/**
 * MainMenu - Main menu screen with title and options
 */

import React, { useState } from 'react';
import { playUIClick, playUIHover } from '../../game/audio/SoundEffects';

interface MainMenuProps {
  onNewGame?: () => void;
  onContinue?: () => void;
  onSettings?: () => void;
  onQuit?: () => void;
  hasSaveData?: boolean;
}

export const MainMenu: React.FC<MainMenuProps> = ({
  onNewGame,
  onContinue,
  onSettings,
  onQuit,
  hasSaveData = false
}) => {
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);

  // Debug: Log hasSaveData value
  console.log(`MainMenu: hasSaveData = ${hasSaveData}`);

  const handleHover = (buttonId: string) => {
    setHoveredButton(buttonId);
    // No sound on hover
  };

  const handleClick = (callback?: () => void, enabled: boolean = true) => {
    if (!enabled) {
      console.log('Button is disabled, ignoring click');
      return;
    }
    playUIClick();
    callback?.();
  };

  // Build menu buttons - Continue is always shown but disabled when no save data
  const menuButtons = [
    { id: 'new-game', label: 'NEW GAME', onClick: onNewGame, enabled: true },
    // Continue button is always visible but greyed out if no save data
    { id: 'continue', label: 'CONTINUE', onClick: onContinue, enabled: hasSaveData },
    { id: 'settings', label: 'SETTINGS', onClick: onSettings, enabled: true },
    { id: 'quit', label: 'QUIT', onClick: onQuit, enabled: true },
  ];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: '#000000',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'monospace',
      color: '#ffffff',
      zIndex: 1000,
    }}>
      {/* Background effect */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'radial-gradient(circle at center, rgba(139, 0, 0, 0.1) 0%, rgba(0, 0, 0, 1) 100%)',
        opacity: 0.5,
      }} />

      {/* Title */}
      <div style={{
        marginBottom: '60px',
        textAlign: 'center',
        zIndex: 1,
      }}>
        <h1 style={{
          fontSize: '72px',
          fontWeight: 'bold',
          color: '#ff0000',
          textShadow: '0 0 30px #ff0000, 0 0 60px #ff0000',
          marginBottom: '10px',
          letterSpacing: '4px',
        }}>
          FIVE NIGHTS
        </h1>
        <h2 style={{
          fontSize: '48px',
          fontWeight: 'bold',
          color: '#ffaa00',
          textShadow: '0 0 20px #ffaa00',
          letterSpacing: '2px',
        }}>
          AT KHAN'S
        </h2>
        <p style={{
          fontSize: '14px',
          color: '#888',
          marginTop: '20px',
          letterSpacing: '2px',
        }}>
          A FNAF-INSPIRED HORROR GAME
        </p>
      </div>

      {/* Menu buttons */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        zIndex: 1,
      }}>
        {menuButtons.map((button) => (
          <button
            key={button.id}
            disabled={!button.enabled}
            onMouseEnter={() => handleHover(button.id)}
            onMouseLeave={() => setHoveredButton(null)}
            onClick={() => handleClick(button.onClick, button.enabled)}
            style={{
              padding: '15px 60px',
              fontSize: '24px',
              fontWeight: 'bold',
              fontFamily: 'monospace',
              backgroundColor: hoveredButton === button.id
                ? 'rgba(255, 0, 0, 0.3)'
                : 'rgba(0, 0, 0, 0.8)',
              color: button.enabled
                ? hoveredButton === button.id ? '#ffffff' : '#ff0000'
                : '#444',
              border: `3px solid ${button.enabled ? '#ff0000' : '#444'}`,
              borderRadius: '8px',
              cursor: button.enabled ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease',
              textShadow: hoveredButton === button.id && button.enabled
                ? '0 0 10px #ff0000'
                : 'none',
              boxShadow: hoveredButton === button.id && button.enabled
                ? '0 0 20px rgba(255, 0, 0, 0.5)'
                : 'none',
              transform: hoveredButton === button.id && button.enabled
                ? 'scale(1.05)'
                : 'scale(1)',
              letterSpacing: '2px',
            }}
          >
            {button.label}
          </button>
        ))}
      </div>

      {/* Version info */}
      <div style={{
        position: 'absolute',
        bottom: '20px',
        right: '20px',
        fontSize: '12px',
        color: '#444',
        zIndex: 1,
      }}>
        v0.1.0 - Alpha Build
      </div>

      {/* Credits */}
      <div style={{
        position: 'absolute',
        bottom: '20px',
        left: '20px',
        fontSize: '12px',
        color: '#444',
        zIndex: 1,
      }}>
        Inspired by Five Nights at Freddy's by Scott Cawthon
      </div>
    </div>
  );
};
